import { Component, ElementRef, HostListener, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommunicationService } from 'app/_services/communication.service';
import { MapService } from 'app/_services/map.service';
import { AppComponent } from 'app/app.component';
import { debounceTime, Subject } from 'rxjs';
import { ILookupValues } from 'app/_models/ILookupValues';
import { IGridConfig } from 'app/_models/IGridConfig';
import { ILine } from 'app/_models/ILine';
import { IFilter } from 'app/_models/IFilter';
import { FilterController } from 'app/_controllers/filterController';
import { IHeader } from 'app/_models/IHeader';
import { GridController } from 'app/_controllers/gridController';
import { SelectExpandController } from 'app/_controllers/selectExpandController';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { IGridData } from 'app/_models/IGridData';
import { IMarkerData } from 'app/_models/IMarkerData';
import { IMapMarker } from 'app/_models/IMapMarker';
import { IMapRoute } from 'app/_models/IMapRoute';
import { ISettings } from 'app/_models/ISettings';
import { ITranslations } from 'app/_models/ITranslations';
import { 
  IconDefinition, faTableColumns, faFilter, faArrowUpLong, faArrowDownLong, faInfoCircle,
  faDownLeftAndUpRightToCenter, faCheck, faAngleLeft, faAngleRight, faChevronDown, faFileExport,
  faArrowUpRightFromSquare, faXmark, faPlus, faCopy, faSquareXmark,
  faPaste, faEraser, faCircleDot, faRemove, faEyeSlash, faUpDown
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})
export class MapComponent implements OnInit {  
  //#region Fields  
  @Input() settings: ISettings;  
  @Input() userId: string;
  @Input() username: string;    
  @Input() nodeId: string;
  @Input() translations: ITranslations;
  viewName: string;
  serverDateFormat: string;
  selectedTabIndex: number = 0;
  mainData: ILookupValues[] = [];
  keyValue: string = undefined;
  extendedData: boolean = false;
  showFilters: boolean = false;
  masterConfig: IGridConfig;
  currentConfig: IGridConfig;
  headers: IHeader[] = [];
  lines: ILine[] = [];
  markerData: IMarkerData[] = [];
  mapMarkers: IMapMarker[] = [];
  mapRoutes: IMapRoute[] = [];
  map: google.maps.Map;  
  filterGridWithDelay: boolean = true;
  filterString: string = '';
  filters: IFilter[] = [];  
  selectedLines: ISelectedLine[] = [];
  sortOrder: boolean = true;
  colSortedBy: string;
  sortString: string = '';
  dateFormat: string = '-'; 
  sortChanged: Subject<string> = new Subject<string>();
  filterUpdated: Subject<string> = new Subject<string>();
  loadedFromDirectLink: boolean = false;    
  i = 0;    
  showAdvancedFilters: boolean = false;
  editableFilterString: string = '';
  editableFilterStringError: string = '';
  filteredByEditableFilterString: boolean = false;
  showAdvancedFiltersButton: boolean = false;
  filteredFromHistory: boolean = false;
  initialViewLoad: boolean = true;
  contextMenuVisible: boolean = false;
  contextMenuTop: number = 0;
  contextMenuLeft: number = 0;
  contextMenuType: string;
  contextMenuObject: IFilter | IHeader | ILine;
  selectedText: string;
  //#endregion

  //#region Icons
  faTableColumns: IconDefinition = faTableColumns;
  faFilter: IconDefinition = faFilter;
  faArrowUpLong: IconDefinition = faArrowUpLong;
  faArrowDownLong: IconDefinition = faArrowDownLong;
  faDownLeftAndUpRightToCenter: IconDefinition = faDownLeftAndUpRightToCenter;
  faCheck: IconDefinition = faCheck;
  faAngleLeft: IconDefinition = faAngleLeft;
  faAngleRight: IconDefinition = faAngleRight;
  faChevronDown: IconDefinition = faChevronDown;
  faFileExport: IconDefinition = faFileExport;
  faInfoCircle: IconDefinition = faInfoCircle;
  faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;
  faXmark: IconDefinition = faXmark;
  faPlus: IconDefinition = faPlus;
  faCopy: IconDefinition = faCopy;
  faPaste: IconDefinition = faPaste;
  faEraser: IconDefinition = faEraser;
  faCircleDot: IconDefinition = faCircleDot;
  faRemove: IconDefinition = faRemove;
  faEyeSlash: IconDefinition = faEyeSlash;
  faUpDown: IconDefinition = faUpDown; 
  //#endregion

  constructor(
    private app: AppComponent,
    private mapService: MapService,
    private communicationService: CommunicationService,
    private filterController: FilterController,
    private gridController: GridController,
    private selectExpandController: SelectExpandController
  ) {
    this.communicationService.base_map_loadMap$.subscribe((nodeId) => { 
      this.nodeId = nodeId;  
      this.filterString = '';
      this.sortString = '';   
      this.getMainData();
    })    

    this.filterUpdated.pipe(debounceTime(1250)).subscribe(() => {
      if (this.filterGridWithDelay) this.buildFilterString();      
    });

    this.sortChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.getData();
    });
  }

  ngOnInit(): void {      
    this.getMainData();     
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  initMap(): void {
    let mapOptions = { zoom: 12, center: { lat: -33.9249, lng: 18.4241 }, mapId: '4504f8b37365c3d0' };
    this.map = new google.maps.Map(document.getElementById('map'), mapOptions);
  }

  getMainData(): void {
    let keys = ['nodeId', 'userId', 'viewName', 'keyfield', 'filterString', 'sortString', 'username'];
    let values = [this.nodeId, this.userId, 'master', '', '', '', this.username];
    let formData = this.app.buildForm(keys, values);
    this.mapService.getData(formData).subscribe({
      next: (data: { resultData: ILookupValues[], masterConfig: IGridConfig }) => {
        this.masterConfig = data.masterConfig;
        this.mainData = data.resultData;
        this.viewName = this.masterConfig.childGrids[this.selectedTabIndex].name;
        
        this.app.hideLoader();        
        if (!localStorage.getItem('qx_direct')) return;
        this.communicationService.loadRadButtons('master', this.nodeId); 
        let id = JSON.parse(localStorage.getItem('qx_direct')).lineId;
        if (id) this.keyValue = id;         
        localStorage.removeItem('qx_direct');
        this.getData(); 
      }, error: (errorLog) => {
        this.app.hideLoader();
        if (this.settings.detailedErrorSetting) {
          sessionStorage.setItem('errorDetails', JSON.stringify(errorLog.error));
          this.app.router.navigate(["error"]);
          return;
        }

        console.log(errorLog);
        this.app.alertError(`${this.translations.ALERT_Error_ErrorWithRefNumberOccured}: ${errorLog.error.refNumber}`);
      }
    });
  }

  changeTab(event: any): void {
    this.filterString = ''; 
    this.sortString = '';   
    this.unFocusAllFilters();
    this.selectedTabIndex = event.index;
    this.filters = [];
    this.showFilters = false;
    this.editableFilterString = '';
    this.showAdvancedFilters = false;
    this.filteredByEditableFilterString = false;
    this.viewName = this.masterConfig.childGrids[this.selectedTabIndex].caption;    
    this.getData();
  }
  
  getData(): void {
    this.initMap();    
    if (localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`)) 
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`)).serverString;

    if (localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`))
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)).filterString;  

    if (localStorage.getItem(`qx_sort_${this.nodeId}_${this.viewName}`))
      this.sortString = JSON.parse(localStorage.getItem(`qx_sort_${this.nodeId}_${this.viewName}`)).sortString;            
    let keys = ['nodeId', 'userId', 'viewName', 'keyfield', 'filterString', 'sortString', 'username'];
    let values = [this.nodeId, this.userId, this.viewName, this.keyValue, this.filterString, this.sortString, this.username];
    let formData = this.app.buildForm(keys, values);
    this.mapService.getData(formData).subscribe({
      next: (data: IGridData) => {
        this.serverDateFormat = data.dateFormat;
        this.headers = data.headers;              

        if (localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`)) {
          this.editableFilterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`)).userString;
          this.showAdvancedFilters = true;
          this.showFilters = false;            
          this.filters = this.filterController.clearAllFilters(this.filters);
        }

        if (localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)) {           
          this.filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)).filters;            
          this.showFilters = this.filterString.length > 0 ? true : false;  
          if (this.showFilters) this.refocusFilters();          
        } 
        
        if (this.filters.length == 0) 
          this.filters = this.filterController.loadFilters(this.headers); 
        
        let result = this.gridController.formatSanitise(data.lines, this.headers, this.filters, this.settings.dateFormatSetting, this.nodeId, this.viewName);        
        this.headers = result.headers;
        this.lines = result.lines;     
        this.filters = result.filters;
        this.communicationService.updateRadHeaders(this.headers, this.serverDateFormat);

        if (localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`))
          localStorage.setItem(`qx_filters_${this.nodeId}_${this.viewName}`, JSON.stringify({filterString: this.filterString, filters: this.filters}));            
                                          
        this.populateMapFromConfig();
        this.sortOrder = this.sortString && this.sortString.includes('asc') ? true : false;        
      }, error: (errorLog) => {        
        this.gridController.handleGridError(errorLog, this.nodeId, this.viewName, undefined, this.settings);
        this.getData();
      }
    });
  }

  populateMapFromConfig(): void {
    let currentConfig = this.masterConfig.childGrids[this.selectedTabIndex];
    this.markerData = [];
    if (currentConfig.viewType == 'Markers') {
      this.lines.forEach(line => {                
        this.markerData.push({ 
          location: { 
            lat: Number((line.list)['TRK__LAST_LOC'].split(' ')[0]),
            lng: Number((line.list)['TRK__LAST_LOC'].split(' ')[1])
          }, 
          lastUpdated: line.list['TRK__LAST_UPDATE'], 
          name: line.list['SCN__NAME'], 
          id: line.list['SCN__ID'], 
          label: '' 
        });        
      });
      
      this.markerData.forEach(marker => { this.createMapMarker(marker) });
    }

    if (currentConfig.viewType == 'Routes') {
      this.i = 0;
      this.loadTrips();
    }
  }
    
  async createMapMarker(markerData: IMarkerData) {
    let { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    let pin = new PinElement({
      background: '#FFFFFF00',
      borderColor: '#FF0000',
      glyph: markerData.name,
      glyphColor: "black",
    });

    let marker = new AdvancedMarkerElement({
      map: this.map,
      position: markerData.location,
      content: pin.element,
      title: markerData.label,
    });

    let mapMarker: IMapMarker = {
      marker: marker,
      pin: pin,
      selected: false,
      title: markerData.label,
      id: markerData.id
    }

    marker.addListener('click', () => {
      let markerToToggle = this.mapMarkers.find(i => i.id == mapMarker.id);
      if (markerToToggle) {
        this.mapMarkers.forEach(marker => {
          if (marker.id != markerToToggle.id) {
            marker.selected = false;
            marker.pin.borderColor = '#FF0000';
          }
        })

        markerToToggle.selected = !markerToToggle.selected;
        markerToToggle.pin.borderColor = markerToToggle.selected ? '#72b92b' : '#FF0000';

        let linetoSelect = this.lines.find(line => line.rowId == markerToToggle.id);
        if (linetoSelect) {
          linetoSelect.selected = markerToToggle.selected;
          this.selectedLines = linetoSelect.selected ? [{ id: linetoSelect.rowId, lineData: linetoSelect.list, lineNumber: linetoSelect.lineNumber }] : [];                        
          this.communicationService.filterRadButtons(this.masterConfig.childGrids[this.selectedTabIndex].name, this.selectedLines);
        }

        this.lines.forEach(line => { if (line.rowId != linetoSelect.rowId) line.selected = false; })
      }
    });

    this.mapMarkers.push(mapMarker);
  }
      
  loadTrips(): void {
    if (this.i == this.lines.length || this.lines.length == 0) return;
    if (this.i < this.lines.length) {
      let line = this.lines[this.i];
      let routeOriginLat = Number(line.list['TRP__LOCATION_ORIGIN'].split(', ')[0]);
      let routeOriginLng = Number(line.list['TRP__LOCATION_ORIGIN'].split(', ')[1]);
      let routeDestinationLat = Number(line.list['TRP__LOCATION_DESTINATION'].split(', ')[0]);
      let routeDestinationLng = Number(line.list['TRP__LOCATION_DESTINATION'].split(', ')[1]);
      this.createMapMarkerRoutePoints(routeOriginLat, routeOriginLng, '', false);
      this.createMapMarkerRoutePoints(routeDestinationLat, routeDestinationLng, '', false);
      
      let keys = ['nodeId', 'userId', 'viewName', 'keyfield', 'filterString', 'sortString', 'username'];
      let values = [this.nodeId, this.userId, this.viewName, line.rowId, '', '', this.username];
      let formData = this.app.buildForm(keys, values);
      this.mapService.getData(formData).subscribe({
        next: (data: IGridData) => {          
          let waypoints = data.lines.length > 0 ? data.lines.map((stop) => {
            let latLng = stop.list['STP__LOCATION'].split(', ');
            this.createMapMarkerRoutePoints(Number(latLng[0]), Number(latLng[1]), stop.list['STP__NAME'], true);
            return {
              location: { lat: Number(latLng[0]), lng: Number(latLng[1]) },
              stopover: true
            };
          }) : [];

          let request: google.maps.DirectionsRequest = {
            origin: { lat: routeOriginLat, lng: routeOriginLng },
            destination: { lat: routeDestinationLat, lng: routeDestinationLng },
            waypoints: waypoints.length > 0 ? waypoints : undefined,
            travelMode: google.maps.TravelMode.DRIVING
          };

          let directionsService = new google.maps.DirectionsService();
          let directionsRenderer = new google.maps.DirectionsRenderer({
            suppressMarkers: true,
            suppressPolylines: true
          });

          directionsService.route(request, (result: any, status: any) => {
            if (status == google.maps.DirectionsStatus.OK) {
              directionsRenderer.setDirections(result);

              let routePolyline = new google.maps.Polyline({
                path: result.routes[0].overview_path,
                strokeColor: '#73b9ff',
                strokeOpacity: 0.8,
                strokeWeight: 6
              });

              routePolyline.setMap(this.map);
              google.maps.event.addListener(routePolyline, 'click', () => {
                let route = this.mapRoutes.find(r => r.id == line.rowId);
                if (route) {
                  this.mapRoutes.forEach(mapRoute => {
                    if (mapRoute.id != route.id) {
                      mapRoute.selected = false;
                      mapRoute.polyLine.setOptions({
                        strokeColor: mapRoute.selected ? '#72b92b' : '#73b9ff'
                      });
                    }
                  })

                  route.selected = !route.selected;
                  route.polyLine.setOptions({ strokeColor: route.selected ? '#72b92b' : '#73b9ff' });
                  let linetoSelect = this.lines.find(line => line.rowId == route.id);
                  if (linetoSelect) {
                    this.lines.forEach(line => { if (line.rowId != linetoSelect.rowId) { line.selected = false; } })
                    linetoSelect.selected = route.selected;
                    this.selectedLines = linetoSelect.selected ? [{ id: linetoSelect.rowId, lineData: linetoSelect.list, lineNumber: linetoSelect.lineNumber }] : [];                    
                    this.communicationService.filterRadButtons(this.masterConfig.childGrids[this.selectedTabIndex].name, this.selectedLines);
                  }
                }
              });

              let mapRoute: IMapRoute = {
                polyLine: routePolyline,
                selected: false,
                id: line.rowId
              }

              this.mapRoutes.push(mapRoute);
              this.i++;
              this.loadTrips();
            }
          });
        }, error: (errorLog) => {
          this.app.hideLoader();
          if (this.settings.detailedErrorSetting) {
            sessionStorage.setItem('errorDetails', JSON.stringify(errorLog.error));
            this.app.router.navigate(["error"]);
            return;
          }

          console.log(errorLog);
          this.app.alertError(`${this.translations.ALERT_Error_ErrorWithRefNumberOccured}: ${errorLog.error.refNumber}`);
        }
      });
    } else {
      this.i++;
      this.loadTrips();
    }
  }

  async createMapMarkerRoutePoints(lat: number, lng: number, title: string, isStop: boolean) {
    let { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    let pin = new PinElement({
      background: '#FFFFFF00',
      borderColor: '#FF0000',
      glyph: title,
      glyphColor: "black",
      scale: !isStop ? 1 : 0.8
    });

    let marker = new AdvancedMarkerElement({
      map: this.map,
      position: { lat: lat, lng: lng },
      content: pin.element,
      title: title
    });
  }

  //#region Grid Layout
  hideColumn(header: IHeader): void {
    let visibleCount = this.headers.filter(header => header.visible).length;
    if (visibleCount == 1) {
      this.app.alertError(this.translations.ALERT_Error_AllHeadersHidden);
      return;
    };

    header.visible = false;
    if (this.filters.length > 0) {
      let filterIndex = this.filters.findIndex(filter => filter.field == header.field);
      if (filterIndex != -1) this.filters.splice(filterIndex, 1);
      let filterString = this.filterController.buildFilterString(this.filters);      
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.viewName}`, JSON.stringify({filterString: filterString, filters: this.filters}));
      this.getData(); 
    }

    this.gridController.saveLayout(this.nodeId, this.viewName, this.username, this.headers).then(() => { return; });
  }    
  //#endregion

  //#region Sort & Filter  
  sort(header: IHeader): void {    
    this.headers.forEach(header => header.sortedBy = false);

    if (!header) {
      localStorage.removeItem(`qx_sort_${this.nodeId}_${this.viewName}`);
      this.sortString = '';
      this.sortOrder = true;
      this.getData();
      return;
    }

    if (header.displayFormat == 'IMAGE' || header.dataType == 'Byte[]') return;
    header.sortedBy = !header.sortedBy;
    if (header.sortedBy) this.sortOrder = !this.sortOrder;   
    this.sortString = this.sortOrder ? `${header.field} asc` : `${header.field} desc`;
    localStorage.setItem(`qx_sort_${this.nodeId}_${this.viewName}`, JSON.stringify({ header, sortString: this.sortString }));
    this.getData();
  }

  updateValue(value: string, filter: IFilter, event: any): void {
    filter.isActive = true;
    filter.isNull = false;
    filter.updated = true;    
    filter.value = value == undefined ? event.checked : value;    
    if (filter.value.length == 0) filter.isActive = false;
    
    if (filter.displayFormat != 'CHECK') {
      if (filter.value && filter.history) {
        filter.filteredHistory = filter.history.filter((entry: string) =>
          entry.toLowerCase().includes(filter.value.toLowerCase())
        );
      } else filter.filteredHistory = [...filter.history];                  
      if (this.filterGridWithDelay ) this.filterUpdated.next('');
    } else this.buildFilterString();  
  }

  updateFilterFromHistory(value: string, filter: IFilter): void {
    filter.isActive = true;
    filter.focused = false;
    filter.isNull = false;
    filter.updated = true; 
    filter.value = value;
    filter.showHistory = false;

    if (filter.value && filter.history) {
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(value.toLowerCase())
      );
    } else filter.filteredHistory = [...filter.history];
         
    this.buildFilterString();
  }

  buildFilterString(): void {
    let filter = this.filters.find(filter => filter.updated);    
    if (filter) {
      if (filter.history.length >= 0 && !filter.history.find(historyVal => historyVal == filter.value) && filter.value.length > 0) {
        filter.history.unshift(filter.value);
        if (filter.history.length > 5) filter.history.pop();
      }
    }
   
    this.filterString = this.filterController.buildFilterString(this.filters);
    filter.updated = false;  
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    this.showAdvancedFilters = false;  
    localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`);
    localStorage.setItem(`qx_filters_${this.nodeId}_${this.viewName}`, JSON.stringify({filterString: this.filterString, filters: this.filters}));
    this.getData();
  }

  nullifyFilter(filter: IFilter, event: MouseEvent): void {          
    if (filter.displayFormat.includes('DATE') || filter.displayFormat == 'TIME') {
      event.preventDefault();
      filter = this.filterController.clearFilter(filter);
    }
      
    filter.isNull = true;
    filter.isActive = true;
    filter.updated = true;    
    this.buildFilterString();
  } 

  clearFilter(filter: IFilter): void {
    filter = this.filterController.clearFilter(filter); 
    if (this.filters.filter(f => f.isActive).length == 0) {
      this.toggleFilters();
      return;
    }   

    this.buildFilterString();
  }

  copyValue(filter: IFilter): void {
    this.app.clipboard.copy(filter ? filter.value : this.selectedText);    
    this.app.alertInfo(this.translations.ALERT_Info_Copied);   
  } 

  pasteValueIntoFilter(filter: IFilter): void {
    navigator.clipboard.readText().then(text => {
      filter.value = text;
      filter.isActive = true;
      filter.isNull = false;
      filter.updated = true;
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(text.toLowerCase())
      );      
      this.buildFilterString();
    }).catch();
  }

  fromDateUpdated(selectedDate: string, filter: IFilter):void {
    filter.ranges.serverDateFrom = selectedDate;    
    filter.ranges.userDateFrom = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);    
  }

  toDateUpdated(selectedDate: string, filter: IFilter): void {
    filter.ranges.serverDateTo = selectedDate;    
    filter.ranges.userDateTo = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);    
  } 

  createRangeFilter(filter: IFilter): void {   
    if (filter.displayFormat == 'TIME' && filter.ranges.from.length == 0 && filter.ranges.to.length == 0) {
      this.app.alertError(this.translations.ALERT_Error_InvalidDateRange);
      return;
    } 
    
    if (filter.displayFormat != 'TIME' && (filter.ranges.serverDateFrom == '' || filter.ranges.serverDateTo == '' || filter.ranges.serverDateFrom == null || filter.ranges.serverDateTo == null)) {
      this.app.alertError(this.translations.ALERT_Error_InvalidDateRange);
      return;
    }
    
    filter.isNull = false;
    filter.isActive = true;    
    filter.updated = true;
    this.buildFilterString();  
  }

  getFilterTooltip(filter: IFilter): string {
    if (filter.displayFormat == 'TIME') return `${filter.ranges.from} - ${filter.ranges.to}`;
    return `${filter.ranges.userDateFrom} - ${filter.ranges.userDateTo}`;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;

    if (!this.showFilters) {      
      this.filters = this.filterController.clearAllFilters(this.filters);      
      this.filterString = '';
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.viewName}`, JSON.stringify({filterString: this.filterString, filters: this.filters}));
      this.getData();      
    }  
  }

  preSelectCheckBoxFilter(filter: IFilter): boolean {
    if (!filter.isActive) return false;    
    return this.app.tryParseBoolean(filter.value);
  }  
  
  validateFilterString(filterStringFromInput: string): void {
    this.editableFilterStringError = '';
    let results = this.filterController.formatFilterStringDates(filterStringFromInput, this.filters, this.settings.dateFormatSetting);

    if (results.editableFilterStringError == '') {      
      let result = this.filterController.validateFilterString(results.userString, this.headers, false);
      if (result.includes('ERROR')) {
        this.editableFilterStringError = result;
        return;
      }
      
      this.filteredByEditableFilterString = true;
      localStorage.setItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`, JSON.stringify({userString: results.userString, serverString: results.serverString}));
      localStorage.removeItem(`qx_filters_${this.nodeId}_${this.viewName}`);
      this.filteredByEditableFilterString = true;
      this.filterString = results.serverString;
      this.getData();
    } else this.editableFilterStringError = results.editableFilterStringError;
  } 

  openAdvancedFilters(): void {
    this.showAdvancedFilters = true;
    this.editableFilterString = this.filterController.createEditableFilterString(this.filters);
  }

  closeAdvancedFilters(): void {
    this.showAdvancedFilters = false;
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.viewName}`);     
    this.filterString = '';      
    this.getData();
  }   

  showAdvancedFiltersDialog(): void {
    this.communicationService.showAdvancedFilterDialog(this.filters, this.headers, this.editableFilterString, this.viewName, this.serverDateFormat);
  }    
    
  focusFilter(filter: IFilter): void {    
    filter.focused = !filter.focused;       
    filter.showHistory = filter.focused;
    filter.filteredHistory = filter.history;
    this.filters.forEach(f => { if (f != filter) f.focused = false; });
  }

  unFocusAllFilters(): void {
    if (localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)) {           
      let filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)).filters;
      let filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}_${this.viewName}`)).filterString;                                        
      filters.forEach(filter => {
        filter.focused = false;
        filter.showHistory = false;
      });  
      localStorage.setItem(`qx_filters_${this.nodeId}_${this.viewName}`, JSON.stringify({filterString: filterString, filters: this.filters}));
    }   
  }

  @ViewChildren('inputRef') inputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  refocusFilters(): void {
    this.inputRefs.changes.subscribe((refs: QueryList<ElementRef>) => {
      let focusedItemIndex = this.filters.findIndex(item => item.focused);      
      if (focusedItemIndex !== -1) {        
        this.filters[focusedItemIndex].showHistory = false;
        let input = refs.get(focusedItemIndex);
        input?.nativeElement.focus();
      }
    });
  }      
  //#endregion  

  //#region Selection & Expansion
  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent): void {    
    if (event.key == 'Enter') {
      this.filterGridWithDelay = false;
      this.buildFilterString(); 
    }
  }

  selectLines(line: ILine): void {
    line.selected = !line.selected;
    let viewName = this.masterConfig.childGrids[this.selectedTabIndex].name;
    let result = this.selectExpandController.selectLines(line, false, false, this.selectedLines, this.lines, '', '', false);
    this.selectedLines = result.selectedLines;
    this.lines = result.lines;
    this.communicationService.filterRadButtons(viewName, this.selectedLines);

    if (viewName == 'Devices') {
      let marker = this.mapMarkers.find(mapMarker => mapMarker.id == line.rowId);
      if (marker) {
        marker.selected = line.selected;
        marker.pin.borderColor = marker.selected ? '#72b92b' : '#FF0000';
      }      
    }

    if (viewName == 'Trips') {
      let route = this.mapRoutes.find(mapRoute => mapRoute.id == line.rowId);
      if (route) {
        route.selected = line.selected;
        route.polyLine.setOptions({
          strokeColor: route.selected ? '#72b92b' : '#73b9ff'
        });
      }      
    }
  }

  extendData(): void { 
    this.extendedData = !this.extendedData; 
  }

  expandTile(line: ILine): void { 
    line.expanded = !line.expanded; 
  }
  //#endregion 

  //#region Misc  
  exportData(): void {    
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showExportDialog(this.headers, this.lines, this.viewName, this.keyValue, this.filterString);
  }

  openColumnDialog(): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showColumnDialog(this.headers, this.viewName);
  }  

  selectValue(event: any): void {
    this.keyValue = event.srcElement.value.toString();        
    this.getData();
  }    

  @ViewChild('contextMenu') contextMenuRef!: ElementRef;
  openContextMenu(event: any, obj: IFilter | IHeader): void {
    event.preventDefault();
    this.contextMenuTop = -9999;
    this.contextMenuLeft = -9999;
    this.contextMenuVisible = true;
    this.contextMenuObject = obj;

    setTimeout(() => {
      let menu = this.contextMenuRef.nativeElement as HTMLElement;
      let menuWidth = menu.offsetWidth;
      let menuHeight = menu.offsetHeight;
      let viewportWidth = window.innerWidth;
      let viewportHeight = window.innerHeight;

      let left = event.clientX;
      let top = event.clientY;      
      if (left + menuWidth > viewportWidth) left = viewportWidth - menuWidth - 10;     
      if (top + menuHeight > viewportHeight) top = viewportHeight - menuHeight - 10;
      this.contextMenuLeft = Math.max(0, left);
      this.contextMenuTop = Math.max(0, top);

      switch (obj.kind) {
        case 'IFilter':
          this.contextMenuType = 'filter';
          break;
        case 'IHeader':
          this.contextMenuType = 'header';
          break;      
      }
    }, 0);        
  }

  @HostListener('document:click')
  closeContextMenu(): void { 
    this.contextMenuVisible = false; 
  }
  //#endregion  
}
